import { useCallback, useState } from 'react';

const STORAGE_KEY = 'ctr.chatMode';

/**
 * Three ways to have the same conversation. They differ in one thing only:
 * who decides your turn is over.
 *
 *   type         — you do, by tapping send.
 *   walkie       — you do, by saying the send word. Hands-free, but it can
 *                  never cut you off mid-thought.
 *   conversation — it does, when you go quiet. Fastest back-and-forth, and
 *                  the one that can misread a thinking pause as "your turn".
 */
export const MODES = {
  type: {
    id: 'type',
    label: 'Type',
    sub: 'Keyboard',
    hint: 'Type your message — or tap the mic to dictate it. Replies come back as text.',
    handsFree: false,
  },
  walkie: {
    id: 'walkie',
    label: 'Walkie-talkie',
    sub: 'You hold the floor',
    hint: 'Talk as long as you like. Say your send word (“over over”) or tap Send to hand over.',
    handsFree: true,
  },
  conversation: {
    id: 'conversation',
    label: 'Conversation',
    sub: 'Pause to send',
    hint: 'Just talk. A pause ends your turn, it answers out loud, and the mic reopens.',
    handsFree: true,
  },
};

export const MODE_ORDER = ['type', 'walkie', 'conversation'];

/** Chat interaction mode, remembered across sessions. */
export default function useChatMode() {
  const [mode, setModeState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return MODES[saved] ? saved : 'type';
    } catch {
      return 'type';
    }
  });

  const setMode = useCallback((next) => {
    if (!MODES[next]) return;
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private mode — the choice just won't persist.
    }
  }, []);

  return [mode, setMode];
}
